import { Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";

import { UserContext } from "../../../Context/UserContext";
import { EnhancedIvrState, IvrEvent } from "../../../Models/IvrState";
import { Error } from "../../../Utils/Errors";
import { useApi } from "../../../Utils/useApi";
import useInterval from "../../../Utils/useInterval";

interface Props {
  focusIvrContact?: (ivrContactId: string) => void;
  updateLeadIvrContactId?: (phoneNumber: string, ivrContactId: number) => void;
}

// timeout in seconds for ivr long poll and reconnection re-attempts, default 10
const timeout = process.env.REACT_APP_EVENT_TIMEOUT ? Number(process.env.REACT_APP_EVENT_TIMEOUT) : 10;

const IvrConnection: React.FC<Props> = ({ focusIvrContact, updateLeadIvrContactId }: Props) => {
  const { user, ivrState, setIvrState } = useContext(UserContext);
  const [timestamp, setTimestamp] = useState(0);
  const [ivrSessionResponse, setIvrSessionResponse] = useState<unknown>();

  const { response: getEventsResponse, error: getEventsError } = useApi<IvrEvent[]>({
    url: `/legacy/v0/user/me/ivr/event?timeout=${timeout}`,
    config: { headers: { timestamp } },
    renewsToken: false,
    valid: !!ivrSessionResponse,
    error: Error.GetIvrEvents
  });
  // ON GET EVENTS SUCCESS
  useEffect(() => {
    if (getEventsResponse) {
      // if agent state changed update the context
      const agentStateEvent = getEventsResponse.find(({ type }) => type === "AgentState");
      if (agentStateEvent) setIvrState(new EnhancedIvrState(agentStateEvent));

      // if IVR chat tab changed update  the context
      const chatSwitchedEvent = getEventsResponse.find(
        ({ type, status }) => type === "TextContactEvent" && status === "Active"
      );
      if (chatSwitchedEvent && focusIvrContact) focusIvrContact(chatSwitchedEvent.contactID);

      // if IVR starts dialing a new number and lead contactId is missing
      const dialingEvent = getEventsResponse.find(
        ({ type, status }) => type === "CallContactEvent" && status === "Dialing"
      );
      if (dialingEvent && updateLeadIvrContactId)
        updateLeadIvrContactId(dialingEvent.dnis, Number(dialingEvent.contactID));

      // restart long poll
      setTimestamp(new Date().getUTCMilliseconds());
    }
  }, [getEventsResponse, focusIvrContact, updateLeadIvrContactId]);
  // ON GET EVENTS ERROR
  useEffect(() => {
    if (getEventsError?.response) {
      const { status } = getEventsError.response;
      // IF 304 RESPONSE POLL AGAIN
      if (status === 304) setTimestamp(new Date().getUTCMilliseconds());
      // ELSE NOTIFY THE USER AND STOP
      else setIvrState({ state: "Disconnected" });
    }
  }, [getEventsError]);

  return (
    <>
      <Typography variant="body2">{`${ivrState.state}${ivrState.reason ? ` - ${ivrState.reason}` : ""}`}</Typography>
      {ivrState.state === "Disconnected" && (
        <Reconnect setIvrSessionResponse={setIvrSessionResponse} userId={user.id} />
      )}
    </>
  );
};

// internal component that re-attempts connection to the IVR on an interval based on the timeout
interface ReconnectProps {
  setIvrSessionResponse: (response: unknown) => void;
  userId: string;
}
const Reconnect: React.FC<ReconnectProps> = ({ setIvrSessionResponse }: ReconnectProps) => {
  const [timestamp, setTimestamp] = useState(0);
  useInterval(() => setTimestamp(new Date().getUTCMilliseconds()), timeout * 1000);
  const { response } = useApi({
    url: "Legacy/V0/User/Me/IVR/Session",
    config: { headers: { timestamp } },
    renewsToken: false,
    error: Error.GetIvrSession
  });
  // on ivr login success start/resume polling events
  useEffect(() => setIvrSessionResponse(response), [response, setIvrSessionResponse]);

  return <></>;
};

export default IvrConnection;
